"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

type Service = {
  title: string;
  description: string;
  image?: {
    src: string;
    className: string;
    alt: string;
  };
};

const services: Service[] = [
  {
    title: "Company Formation",
    description:
      "Get help with registration, documents, and the setup steps needed to start properly.",
    image: {
      src: "/home-v3/group-144.png",
      className: "absolute top-44 left-10 z-20 h-[195px] w-[275px]",
      alt: "Company formation illustration",
    },
  },
  {
    title: "Company Secretary",
    description:
      "Keep statutory records, annual returns, and company maintenance easier to manage.",
    image: {
      src: "/home-v3/group-145.png",
      className: "absolute top-[198px] left-[30px] z-20 h-[171px] w-[302px]",
      alt: "Company secretary illustration",
    },
  },
  {
    title: "Bookkeeping",
    description:
      "Keep monthly records, transactions, invoices, receipts, and reconciliations in order.",
    // illustration: "bookkeeping",
     image: {
      src: "/home-v3/group-146.png",
      className: "absolute top-[198px] left-[30px] z-20 h-[171px] w-[302px]",
      alt: "Company secretary illustration",
    },
  },
  {
    title: "Accounting & Reporting",
    description:
      "Get clear reports that show revenue, expenses, and how your business is performing.",
    // illustration: "accounting",
      image: {
      src: "/home-v3/group-150.png",
      className: "absolute top-[198px] left-[30px] z-20 h-[171px] w-[302px]",
      alt: "Company secretary illustration",
    },
  },
  {
    title: "Tax Filing",
    description:
      "Prepare for tax filing with organized records and guided filing support.",
    image: {
      src: "/home-v3/group-151.png",
      className: "absolute top-[182px] left-[55px] z-20 h-[188px] w-[262px]",
      alt: "Tax filing illustration",
    },
  },
  {
    title: "Ongoing Compliance",
    description:
      "Stay on top of deadlines, company obligations, and compliance responsibilities.",
    image: {
      src: "/home-v3/auto-added-frame.svg",
      className: "absolute bottom-0 -left-10 z-20 h-[227px] w-[312px]",
      alt: "Ongoing compliance illustration",
    },
  },
];

const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const Glow = () => (
  <>
    <div
      aria-hidden="true"
      className="absolute -left-[119px] top-[334px] h-72 w-[605px] rounded-[302.5px/144px] bg-[#0061fe] blur-[137.02px]"
    />
    <div
      aria-hidden="true"
      className="absolute -top-[19px] left-[calc(50%_+_144px)] h-[171px] w-[171px] rounded-full bg-[#6fa6ff] blur-[71.73px]"
    >
      <div className="absolute left-[calc(50%_-_86px)] top-0 h-[105px] w-[171px] rounded-[85.61px/52.64px] bg-[#d2e3ff] blur-[71.73px]" />
      <div className="absolute left-[calc(50%_-_86px)] top-0 h-[89px] w-[171px] rounded-[85.61px/44.54px] bg-white blur-[37.02px]" />
    </div>
  </>
);

const ServiceCard = ({ service }: { service: Service }) => (
  <motion.div variants={cardVariants}>
    <Card className="relative h-[399px] w-full overflow-hidden rounded-[32px] border-0 bg-[#0f1218] shadow-none">
      <CardContent className="relative z-20 p-0">
        <div className="px-10 pt-6">
          <h3 className="mt-[-1px] whitespace-nowrap [font-family:'Suisse_Intl_Trial-Bold',Helvetica] text-2xl font-bold leading-7 tracking-[0] text-white">
            {service.title}
          </h3>
          <p className="mt-4 max-w-[312px] [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal leading-6 tracking-[0] text-white">
            {service.description}
          </p>
        </div>
      </CardContent>
      <Glow />
      {service.image && (
        <img
          className={service.image.className}
          src={service.image.src}
          alt={service.image.alt}
        />
      )}
    </Card>
  </motion.div>
);

export const ServiceApproachBenefitsSection = (): JSX.Element => {
  return (
    <section
      aria-labelledby="service-approach-title"
      className="mx-auto mt-20 sm:mt-32 flex w-full max-w-[1440px] flex-col items-center gap-[59px] px-4 sm:px-8 lg:px-[108px]"
    >
      <header className="flex w-full max-w-[937px] flex-col items-center">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] tracking-[0] text-white backdrop-blur-[3.5px]">
          What We Handle
        </Badge>
        <motion.h2
          id="service-approach-title"
          className="mt-6 max-w-[937px] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-4xl font-normal leading-[1.15] tracking-[-1.92px] text-white [text-shadow:0px_4px_24px_#0000000d] sm:text-5xl sm:leading-[64px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          The essential services behind a
          <br />
          well-managed company
        </motion.h2>
      </header>
      <motion.div
        className="grid w-full grid-cols-1 gap-x-6 gap-y-[25px] md:grid-cols-2 xl:grid-cols-3"
        variants={cardsContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        {services.map((service) => (
          <ServiceCard key={service.title} service={service} />
        ))}
      </motion.div>
    </section>
  );
};
